import { DatePipe } from "@angular/common";
import { inject, Injectable, signal } from "@angular/core";
import { Basket, BasketGroup, CartAutomationRules, Group, OriginEvent, TicketConfig } from "@core/models";

@Injectable()
export class BasketService {
  private readonly datePipe = inject(DatePipe);

  basket = signal<Basket | null>(null);
  private groups = signal<BasketGroup[]>([]); //list of groups of selected tickets

  addToBasket(basket: Basket): void {
    this.basket.set(basket);
  }

  addEventToBasket(event: OriginEvent, date: Date, ticketConfig: { [key: string]: TicketConfig[] }): void {
    if (!this.basket() || this.datePipe.transform(this.basket().date, "yyyy-MM-dd") === this.datePipe.transform(date, "yyyy-MM-dd")) {
      this.basket.set({
        event,
        date,
        tickets: ticketConfig
      });
    } else {
      this.basket.update((basket) => {
        basket.tickets = ticketConfig;
        return basket;
      });
    }
  }

  removeEventFromBasket(): void {
    this.basket.set(null);
  }

  getCartAutomateRule(groupId: string): CartAutomationRules | undefined {
    return this.basket().event?.cartAutomationRules?.find(rule => rule.triggerTargetGroup === groupId);
  }

  getTicketGroup(ticketId: string): Group | undefined {
    return this.basket().event?.groups?.find(group => group.tickets.findIndex(_ticketId => _ticketId === ticketId) > -1);
  }

  getAdditionalTicketCount(ticketId: string): number {
    const group = this.getTicketGroup(ticketId);
    if (group) {
      const automateRule = this.getCartAutomateRule(group._id);
      if (automateRule) {
        return automateRule.thenTargets?.[0].thenTargetMin ?? 0;
      }
      return 0;
    }
    return 0;
  }

  eachTimeslotPrice(ticketConfig: TicketConfig[]): number {
    return ticketConfig.reduce((total, config) => total + ((Number(config.quantity) + this.getAdditionalTicketCount(config.ticket.id) * Number(config.quantity)) * config.ticket.price), 0);
  }

  eachEventPricie(ticketConfig: { [key: string]: TicketConfig[] }): number {
    return Object.keys(ticketConfig).reduce((total, timeslotId) => total + this.eachTimeslotPrice(ticketConfig[timeslotId]), 0);
  }

  basketPrice(): number {
    return this.eachEventPricie(this.basket()?.tickets ?? {});
  }

  getTimeSlotTicketCount(configList: TicketConfig[]): number {
    return configList?.reduce((total, config) => total + (Number(config.quantity) + this.getAdditionalTicketCount(config.ticket.id) * Number(config.quantity)), 0) ?? 0;
  }

  totalTickets(): number {
    return Object.values(this.basket()?.tickets ?? {}).reduce((total, configList) => total + this.getTimeSlotTicketCount(configList), 0);
  }

  getEventById(): OriginEvent | undefined {
    return this.basket()?.event;
  }

  getTicketConfigByEventId(): TicketConfig[] {
    return this.basket()?.tickets[0];
  }

  getFirstEvent(): OriginEvent | undefined {
    return this.basket()?.event;
  }

  clearBasket(): void {
    this.basket.set(null);
  }

  getSelectedGroups(): BasketGroup[] {
    return this.groups();
  }

  addGroup(groupId: string): void {
    this.groups.update(groups => {
      const index = groups.findIndex(item => item.id === groupId);
      //check if group is already in selectedGroups 
      if (index !== -1) {
        //clone the array and item to trigger change detection
        const updatedGroups = [...groups];
        updatedGroups[index] = { ...updatedGroups[index], quantity: updatedGroups[index].quantity + 1 };
        return updatedGroups;
      } else {
        return [...groups, { id: groupId, quantity: 1 }];
      }
    });
  }

  removeGroup(groupId: string): void {
    this.groups.update(groups => {
      const index = groups.findIndex(item => item.id === groupId);
  
      if (index === -1) return groups;
  
      const updatedGroups = [...groups];
  
      if (updatedGroups[index].quantity > 1) {
        updatedGroups[index] = {
          ...updatedGroups[index],
          quantity: updatedGroups[index].quantity - 1
        };
      } else {
        updatedGroups.splice(index, 1);
      }
  
      return updatedGroups;
    });
  }
}
